import type { BlogCluster, BlogPost, BlogPostSummary } from "./blogTypes";

export const DEFAULT_ORIGIN = "https://fidexa.com.br";
const ORGANIZATION_ID = `${DEFAULT_ORIGIN}/#organization`;
const BLOG_NAME = "Blog da Fidexa";

export interface PostMeta {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
}

const postUrl = (slug: string, origin: string): string => `${origin}/blog/${slug}`;

const resolveTitle = (post: BlogPost): string => post.seo.metaTitle || post.title;
const resolveDescription = (post: BlogPost): string => post.seo.metaDescription || post.excerpt;
/**
 * Imagem de compartilhamento. Posts não têm mais imagem de capa; na falta de um
 * og:image definido à mão, o card é desenhado sob demanda por `/api/og`, com o
 * mesmo visual da capa da listagem (gradiente + mês/ano + marca + título).
 */
const resolveImage = (post: BlogPost, origin: string): string =>
  post.seo.ogImageUrl ||
  `${origin}/api/og?slug=${encodeURIComponent(post.slug)}`;

export const buildPostMeta = (post: BlogPost, origin: string = DEFAULT_ORIGIN): PostMeta => {
  const title = resolveTitle(post);
  const description = resolveDescription(post);
  const url = post.seo.canonicalUrl || postUrl(post.slug, origin);

  return {
    title,
    description,
    canonical: url,
    ogTitle: title,
    ogDescription: description,
    ogImage: resolveImage(post, origin),
    ogUrl: url,
  };
};

export const buildArticleJsonLd = (
  post: BlogPost,
  origin: string = DEFAULT_ORIGIN
): Record<string, unknown> => {
  const url = postUrl(post.slug, origin);
  const image = resolveImage(post, origin);

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: resolveTitle(post),
    description: resolveDescription(post),
    image: [image],
    datePublished: post.publishedAt ?? post.createdAt,
    dateModified: post.updatedAt,
    author: {
      "@type": "Person",
      name: post.authorName || "Equipe Fidexa",
    },
    publisher: { "@id": ORGANIZATION_ID },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    ...(post.category ? { articleSection: post.category } : {}),
    ...(post.tags.length > 0 ? { keywords: post.tags.join(", ") } : {}),
  };
};

/**
 * FAQPage a partir do `faq` estruturado do post.
 *
 * ATENÇÃO AO QUE ISSO É E AO QUE NÃO É: o rich result de FAQ do Google foi
 * restringido a sites de governo e saúde em agosto de 2023 e **desligado de
 * vez em 7 de maio de 2026**. Este markup NÃO traz mais o acordeão na SERP do
 * Google, e o suporte a ele sai do Search Console ao longo de 2026.
 *
 * Emitimos mesmo assim porque o custo é uma função pura e o consumidor mudou:
 * outros buscadores e, principalmente, os rastreadores de IA leem schema para
 * extrair pergunta/resposta. O valor de SEO, hoje, está no FAQ COMO CONTEÚDO
 * na página (bloco `faq`, E1.2) respondendo a pergunta de forma direta — não
 * no JSON-LD. Não priorize este trecho achando que ele rende snippet.
 *
 * Fonte da verdade é `post.faq`. O bloco `data-ds-block="faq"` no corpo é a
 * renderização visível; quem preenche os dois de forma coerente é o estágio
 * `seo` do gerador (E5).
 */
export const buildFaqJsonLd = (post: BlogPost): Record<string, unknown> | null => {
  const items = post.faq.filter(i => i.question.trim() && i.answer.trim());
  if (items.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(i => ({
      "@type": "Question",
      name: i.question.trim(),
      acceptedAnswer: { "@type": "Answer", text: i.answer.trim() },
    })),
  };
};

export const buildBreadcrumbJsonLd = (
  post: BlogPost,
  origin: string = DEFAULT_ORIGIN
): Record<string, unknown> => {
  // A categoria fica de fora: não tem URL própria, e o Google marca como erro
  // crítico qualquer ListItem intermediário sem `item`.
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Início", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: postUrl(post.slug, origin),
      },
    ],
  };
};

export const buildBlogListJsonLd = (origin: string = DEFAULT_ORIGIN): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": `${origin}/blog`,
  name: BLOG_NAME,
  url: `${origin}/blog`,
  inLanguage: "pt-BR",
  publisher: { "@id": ORGANIZATION_ID },
});

// ── HUB DE CLUSTER (E1.5) ───────────────────────────────────────────────────

export const clusterUrl = (slug: string, origin: string = DEFAULT_ORIGIN): string =>
  `${origin}/blog/cluster/${slug}`;

/**
 * O hub existe para concentrar autoridade de um tema e distribuir link para os
 * satélites. Por isso o título sai da `headKeyword` (a intenção de busca de
 * cabeça), e não do nome interno do cluster: quem procura digita a keyword.
 */
export const buildClusterMeta = (
  cluster: BlogCluster,
  origin: string = DEFAULT_ORIGIN
): PostMeta => {
  const title = `${cluster.headKeyword}: guias e conteúdos | Fidexa`;
  const description =
    cluster.description ||
    `Conteúdos da Fidexa sobre ${cluster.headKeyword} para lojistas que vendem no crediário próprio.`;
  const url = clusterUrl(cluster.slug, origin);

  return {
    title,
    description,
    canonical: url,
    ogTitle: title,
    ogDescription: description,
    ogImage: `${origin}/og-image.png`,
    ogUrl: url,
  };
};

/**
 * `CollectionPage` + `ItemList` na mesma página: o primeiro diz o que a página
 * é, o segundo enumera os posts na ordem em que aparecem. É essa enumeração
 * que ajuda o buscador a entender o hub como índice do tema, em vez de mais
 * uma listagem paginada.
 */
export const buildClusterJsonLd = (
  cluster: BlogCluster,
  posts: BlogPostSummary[],
  origin: string = DEFAULT_ORIGIN
): Record<string, unknown> => {
  const url = clusterUrl(cluster.slug, origin);

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": url,
    url,
    name: cluster.headKeyword,
    description: cluster.description,
    inLanguage: "pt-BR",
    isPartOf: { "@id": `${origin}/blog` },
    publisher: { "@id": ORGANIZATION_ID },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: posts.length,
      itemListElement: posts.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: postUrl(p.slug, origin),
        name: p.title,
      })),
    },
  };
};

export const buildClusterBreadcrumbJsonLd = (
  cluster: BlogCluster,
  origin: string = DEFAULT_ORIGIN
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Início", item: `${origin}/` },
    { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
    {
      "@type": "ListItem",
      position: 3,
      name: cluster.name,
      item: clusterUrl(cluster.slug, origin),
    },
  ],
});
